import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { apiUrl } from "../utils/config";

// ── Singleton cache ───────────────────────────────────────────────────────────
// API shape: { key: { lang: value } }
// e.g. { add_to_cart: { en: "Add to Cart", es: "Añadir al carrito" } }
let _cache = null;
let _pending = null;
const _listeners = new Set();

const LANG_KEY    = "app_lang";
const DEFAULT_LANG = "en";

function notify(data) {
  _listeners.forEach((fn) => fn(data));
}

async function fetchAll() {
  if (_cache)   return _cache;
  if (_pending) return _pending;

  _pending = axios
    .get(`${apiUrl}/translations`)
    .then((res) => {
      _cache = res.data?.data || res.data || {};
      notify(_cache);
      return _cache;
    })
    .catch(() => {
      _cache = {};
      notify(_cache);
      return {};
    })
    .finally(() => { _pending = null; });

  return _pending;
}

/** Call after admin saves to push fresh data to all consumers. */
export function invalidateTranslationCache() {
  _cache = null;
  fetchAll();
}

// ── Hook ─────────────────────────────────────────────────────────────────────
export function useTranslations() {
  const [translations, setTranslations] = useState(_cache || {});
  const [lang, setLangState] = useState(
    () => localStorage.getItem(LANG_KEY) || DEFAULT_LANG
  );
  const [isLoading, setIsLoading] = useState(!_cache);

  useEffect(() => {
    const update = (data) => { setTranslations(data); setIsLoading(false); };
    _listeners.add(update);

    if (!_cache) {
      setIsLoading(true);
      fetchAll(); // result delivered via notify()
    } else {
      setTranslations(_cache);
      setIsLoading(false);
    }

    return () => _listeners.delete(update);
  }, []);

  /** Persist language choice. */
  const setLang = useCallback((code) => {
    localStorage.setItem(LANG_KEY, code);
    setLangState(code);
  }, []);

  /**
   * Translate a key.
   *   t('add_to_cart')            → value for current lang
   *   t('add_to_cart', 'es')      → value for a specific lang
   *   t('missing_key', 'Fallback')→ fallback string
   *
   * Priority: requested lang → English fallback → explicit fallback → key itself
   */
  const t = useCallback(
    (key, langOverrideOrFallback) => {
      // Detect whether second arg is a lang code or a fallback string
      const isLangCode =
        langOverrideOrFallback &&
        langOverrideOrFallback.length <= 5 &&
        /^[a-z]{2}(-[a-zA-Z]{2,4})?$/.test(langOverrideOrFallback);

      const targetLang = isLangCode ? langOverrideOrFallback : lang;
      const fallback   = isLangCode ? undefined : langOverrideOrFallback;

      const entry = translations[key];
      if (entry) {
        if (entry[targetLang]) return entry[targetLang];
        if (entry[DEFAULT_LANG]) return entry[DEFAULT_LANG];
      }
      return fallback !== undefined ? fallback : key;
    },
    [translations, lang]
  );

  return { t, lang, setLang, isLoading, translations };
}

export default useTranslations;
